import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Headphones, Send, Clock, CheckCircle, FileText, Upload } from "lucide-react";

export default function SupportPage() {
  const [services, setServices] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ type: "GENERAL", subject: "", description: "", urgency: "normal" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [{ data: s }, { data: r }] = await Promise.all([
          api.get("/support/pricing"),
          api.get("/support/requests")
        ]);
        setServices(s.services);
        setRequests(r);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/support/request-assistance", form);
      alert(`Request submitted! ID: ${data.request_id}\n\nOur team at ${data.email} will respond within 24 hours.`);
      setShowForm(false);
      setForm({ type: "GENERAL", subject: "", description: "", urgency: "normal" });
      const { data: r } = await api.get("/support/requests");
      setRequests(r);
    } catch (e) { alert(e.response?.data?.detail || "Error"); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="space-y-6" data-testid="support-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-semibold text-[#0B1020]">
            <Headphones size={24} className="inline mr-2 text-[#C8A961]" />
            Assisted Services
          </h1>
          <p className="text-sm text-[#6B7280] mt-1">Request professional assistance with integration, reconciliation, training, or audit preparation</p>
        </div>
        <button onClick={() => setShowForm(true)} className="stratum-btn-primary flex items-center gap-2" data-testid="request-assistance-btn">
          <Send size={14} /> Request Assistance
        </button>
      </div>

      {/* Service Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {services.map(s => (
          <div key={s.code} className="stratum-card p-5 hover:shadow-sm transition-shadow" data-testid={`service-${s.code}`}>
            <h3 className="font-display text-base font-semibold text-[#0B1020] mb-1">{s.name}</h3>
            <p className="text-xs text-[#6B7280] mb-3">{s.description}</p>
            <div className="flex justify-between items-center text-xs">
              <span className="font-mono text-[#C8A961] font-semibold">{s.price}</span>
              <span className="text-[#6B7280]">{s.turnaround}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Request Form */}
      {showForm && (
        <div className="stratum-card p-6">
          <h3 className="font-display text-lg font-semibold mb-4">Submit Assistance Request</h3>
          <div className="space-y-4 max-w-xl">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-[#6B7280] mb-1">Service Type</label>
                <select className="stratum-input w-full" value={form.type} onChange={e => setForm({...form, type: e.target.value})} data-testid="support-type-select">
                  <option value="GENERAL">General Enquiry</option>
                  <option value="MIGRATION">FAR Migration</option>
                  <option value="RECONCILIATION">Depreciation Reconciliation</option>
                  <option value="INTEGRATION">Accounting System Integration</option>
                  <option value="TRAINING">Team Training</option>
                  <option value="POLICY_REVIEW">IFRS Policy Review</option>
                  <option value="AUDIT_PREP">Audit Preparation</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-[#6B7280] mb-1">Urgency</label>
                <select className="stratum-input w-full" value={form.urgency} onChange={e => setForm({...form, urgency: e.target.value})}>
                  <option value="low">Low</option>
                  <option value="normal">Normal</option>
                  <option value="high">High (within 48h)</option>
                  <option value="critical">Critical (same day)</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-[#6B7280] mb-1">Subject</label>
              <input className="stratum-input w-full" value={form.subject} onChange={e => setForm({...form, subject: e.target.value})} placeholder="Brief description of what you need help with" data-testid="support-subject-input" />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#6B7280] mb-1">Details</label>
              <textarea className="stratum-input w-full" rows={4} value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="Describe your requirement in detail. Include any relevant file references, entity names, or period dates." data-testid="support-desc-input" />
            </div>
            <p className="text-xs text-[#6B7280]">Your request and any uploaded data within the app will be sent to <strong>info@stratumassetreg.com</strong> for review.</p>
            <div className="flex gap-3">
              <button onClick={() => setShowForm(false)} className="px-4 py-2 rounded-lg border border-[#E8E2D5] text-sm">Cancel</button>
              <button onClick={submit} disabled={submitting || !form.subject} className="stratum-btn-primary disabled:opacity-50" data-testid="submit-support-btn">
                {submitting ? "Submitting..." : "Submit Request"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Previous Requests */}
      {requests.length > 0 && (
        <div className="stratum-card overflow-hidden">
          <div className="p-4 border-b border-[#E8E2D5]"><h3 className="font-display text-base font-semibold">Your Requests</h3></div>
          <table className="w-full stratum-table" data-testid="support-requests-table">
            <thead><tr><th>Date</th><th>Type</th><th>Subject</th><th>Urgency</th><th>Status</th></tr></thead>
            <tbody>
              {requests.map(r => (
                <tr key={r.id}>
                  <td className="font-mono text-xs tabular-nums">{r.submitted_at?.substring(0, 10)}</td>
                  <td className="text-xs">{r.type}</td>
                  <td className="text-sm">{r.subject}</td>
                  <td><span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${r.urgency === "critical" ? "bg-[#991B1B]/10 text-[#991B1B]" : r.urgency === "high" ? "bg-[#92400E]/10 text-[#92400E]" : "bg-[#6B7280]/10 text-[#6B7280]"}`}>{r.urgency}</span></td>
                  <td><span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${r.status === "COMPLETED" ? "bg-[#065F46]/10 text-[#065F46]" : "bg-[#C8A961]/10 text-[#C8A961]"}`}>{r.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
